import { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { DischargeFormPayload } from '@/forms/DischargeForm/types';
import { GraduateFormPayload } from '@/forms/GraduateForm/types';
import {
  EpisodeOfCareDischarge,
  EpisodeOfCareGraduate,
  StartNewPhase,
  UpdateProgramAndPhase
} from '@/repositories/EpisodeOfCareRepository/types';
import { DischargeReason } from '@/types/dischargeReason';
import { EpisodeOfCare } from '@/types/episodeOfCare';
import { GraduationReason } from '@/types/graduationReason';
import RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const episodeOfCareRepository = {
  async getDischargeReasons({ host }: { host: string }): Promise<DischargeReason[]> {
    const path = '/api/dugout/episode_of_care/discharge_reasons/';
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  },

  async getGraduationReasons({ host }: { host: string }): Promise<GraduationReason[]> {
    const path = '/api/dugout/episode_of_care/graduation_reasons/';
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  },

  async discharge({
    host,
    params,
    episodeOfCareId
  }: {
    host: string;
    params: DischargeFormPayload;
    episodeOfCareId: string;
  }): Promise<RepositoryResponse<EpisodeOfCareDischarge>> {
    try {
      const path = `/api/dugout/episode_of_care/${episodeOfCareId}/discharge/`;
      const response = await ajax.post<DischargeFormPayload, AxiosResponse>({
        url: `${host}${path}`,
        data: params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to reject person.');
    }
  },

  async graduate({
    host,
    params,
    episodeOfCareId
  }: {
    host: string;
    params: GraduateFormPayload;
    episodeOfCareId: string;
  }): Promise<RepositoryResponse<EpisodeOfCareGraduate>> {
    try {
      const path = `/api/dugout/episode_of_care/${episodeOfCareId}/graduate/`;
      const response = await ajax.post<GraduateFormPayload, AxiosResponse>({
        url: `${host}${path}`,
        data: params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to graduate person.');
    }
  },

  async updateProgramAndPhase({
    host,
    episodeOfCareId,
    params
  }: {
    host: string;
    episodeOfCareId: string;
    params: {
      program_id: string;
      phase_type_id: string;
    };
  }): Promise<RepositoryResponse<UpdateProgramAndPhase>> {
    try {
      const path = `/api/dugout/episode_of_care/${episodeOfCareId}/update_program_and_phase/`;
      const response = await ajax.patch<
        {
          program_id: string;
          phase_type_id: string;
        },
        AxiosResponse
      >({
        url: `${host}${path}`,
        data: params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to update program and phase.');
    }
  },

  async startNewPhase({
    host,
    episodeOfCareId,
    params
  }: {
    host: string;
    episodeOfCareId: string;
    params: {
      program_id: string;
      phase_type_id: string;
    };
  }): Promise<RepositoryResponse<StartNewPhase>> {
    try {
      const path = `/api/dugout/episode_of_care/${episodeOfCareId}/start_new_phase/`;
      const response = await ajax.patch<
        {
          program_id: string;
          phase_type_id: string;
        },
        AxiosResponse
      >({
        url: `${host}${path}`,
        data: params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to start new phase.');
    }
  },

  async updateDischargeReasons({
    host,
    episodeOfCareId,
    params
  }: {
    host: string;
    episodeOfCareId: string;
    params: Partial<EpisodeOfCare>;
  }): Promise<RepositoryResponse<EpisodeOfCare>> {
    try {
      const path = `/api/dugout/episode_of_care/${episodeOfCareId}/update_discharge_reasons/`;
      const response = await ajax.put<Partial<EpisodeOfCare>, AxiosResponse>({
        url: `${host}${path}`,
        data: params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to update episode of care.');
    }
  }
};

const EpisodeOfCareRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getDischargeReasons(): Promise<DischargeReason[]> {
      return episodeOfCareRepository.getDischargeReasons({ host });
    },

    async getGraduationReasons(): Promise<GraduationReason[]> {
      return episodeOfCareRepository.getGraduationReasons({ host });
    },

    async discharge({
      episodeOfCareId,
      params
    }: {
      episodeOfCareId: string;
      params: DischargeFormPayload;
    }): Promise<RepositoryResponse<EpisodeOfCareDischarge>> {
      return episodeOfCareRepository.discharge({ host, episodeOfCareId, params });
    },

    async graduate({
      episodeOfCareId,
      params
    }: {
      episodeOfCareId: string;
      params: GraduateFormPayload;
    }): Promise<RepositoryResponse<EpisodeOfCareGraduate>> {
      return episodeOfCareRepository.graduate({ host, episodeOfCareId, params });
    },

    async updateProgramAndPhase({
      episodeOfCareId,
      params
    }: {
      episodeOfCareId: string;
      params: {
        program_id: string;
        phase_type_id: string;
      };
    }): Promise<RepositoryResponse<UpdateProgramAndPhase>> {
      return episodeOfCareRepository.updateProgramAndPhase({ host, episodeOfCareId, params });
    },

    async startNewPhase({
      episodeOfCareId,
      params
    }: {
      episodeOfCareId: string;
      params: {
        program_id: string;
        phase_type_id: string;
      };
    }): Promise<RepositoryResponse<StartNewPhase>> {
      return episodeOfCareRepository.startNewPhase({ host, episodeOfCareId, params });
    },

    async updateDischargeReasons({
      episodeOfCareId,
      params
    }: {
      episodeOfCareId: string;
      params: Partial<EpisodeOfCare>;
    }): Promise<RepositoryResponse<EpisodeOfCare>> {
      return episodeOfCareRepository.updateDischargeReasons({ host, episodeOfCareId, params });
    }
  };
};

export default EpisodeOfCareRepository;
